import { defineStore } from 'pinia'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import DataLayer from '@services/data-layer/index'
import { useApi } from '@composables/use-api'
import superPropertiesManager from '@services/mixpanel/super-properties-manager'
import mixpanelService from '@services/mixpanel-service'
import { useMemberStore } from '@stores/member'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        currentUser: localSave.get('auth.currentUser'),
    }),
    getters: {
        loggedIn: state => !!state.currentUser,
    },
    actions: {
        setCurrentUser(user) {
            this.currentUser = user
            localSave.save('auth.currentUser', user)
        },

        async init() {
            await this.setToken()
            this.validate()
        },

        setToken() {
            return useApi().token.set(this.currentUser ? `Bearer ${this.currentUser.token}` : '')
        },

        async setUser(user) {
            this.setCurrentUser(user)
            await this.setToken()

            // Update Mixpanel super properties and identity
            if (user) {
                const memberId = user.members_id?.id ? String(user.members_id.id) : null
                superPropertiesManager.updateUserStatus(true, memberId)
                if (memberId) {
                    mixpanelService.identify(memberId)
                }
            } else {
                superPropertiesManager.updateUserStatus(false, null)
                mixpanelService.reset()
            }
        },

        async logIn(fields = {}) {
            // An explicit credential login must always authenticate with the supplied
            // credentials. Clear any persisted session FIRST: the API rejects every
            // request carrying an invalid Bearer token — including this login POST.
            // Clear only the stored user + token — setUser(null) would also fire
            // mixpanelService.reset() and wipe the pre-login identity/super properties.
            this.setCurrentUser(null)
            await this.setToken()

            const response = await useApi().auth.login({}, fields)
            const user = response.body
            this.setUser(user)
            useMemberStore().init(user)

            return user
        },

        logOut() {
            const clearSession = () => {
                this.setUser(null)
                useMemberStore().destroy()
                localSave.clear('localStorage')
                localSave.clear('sessionStorage')
                window.location.href = '/'
            }

            return useApi()
                .auth.revoke({})
                .then(clearSession)
                .catch(error => {
                    if (error.response.status === 401 || error.response.status === 500) {
                        clearSession()
                    }
                })
        },

        // The persisted session is already dead server-side (401), so unlike logOut
        // there is nothing to revoke. Clear local state and restart the app. The
        // caller may pass the intended destination (mid-navigation, the URL bar
        // hasn't been updated yet); the default covers the boot path, where the
        // current URL is already the right one.
        async expireSession(targetUrl) {
            await this.setUser(null)
            useMemberStore().destroy()
            localSave.clear('localStorage')
            localSave.clear('sessionStorage')
            postAuthRedirect._navigate(targetUrl || window.location.href)
        },

        async validate({ redirectTo }: { redirectTo?: string } = {}) {
            if (!this.currentUser) return null

            // A persisted user we cannot even read an id from is a dead session too.
            if (!this.currentUser.members_id?.id) {
                await this.expireSession(redirectTo)
                return null
            }

            try {
                await DataLayer.members.getPermissionsByMemberId({ id: this.currentUser.members_id.id })
                return true
            } catch (error) {
                if (error?.response?.status === 401) this.expireSession(redirectTo)
                return null
            }
        },
    },
})
